/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { isEmpty } from 'lodash';
import { REAUTHENTICATE_ROUTE, uuidPattern, REAUTHENTICATE_ROUTES } from 'utils/constants';

const RequireSession = ({
    children,
    session,
    onMount = false,
    reauthenticateSession,
    updateSessionToken,
    updateReauthenticateSessionID,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    // to check if "reauthenticate" is included in route or not
    const isReAuthenticateRoute = location?.pathname?.includes(REAUTHENTICATE_ROUTE);

    // function to exact sessionUUID from reauthenticate route and store it and redirect to home route for reauthenticate
    const configureReauthenticateSession = useCallback(() => {
        const values = queryString.parse(location.search);
        const match = location.pathname.match(uuidPattern);

        if (match?.length) {
            // Session start from desktop -> fetch session id from url and store in redux
            updateReauthenticateSessionID(match[0], `/${REAUTHENTICATE_ROUTE}`);
        } else if (values?.sessionUUID) {
            // Session start from desktop and then open in mobile -> fetch session id from params and directly redirect to capture selfie page
            updateReauthenticateSessionID(
                values?.sessionUUID,
                REAUTHENTICATE_ROUTES.CAPTURE_SELFIE
            );
        }
    }, []);

    useEffect(() => {
        let values = queryString.parse(location.search);
        if (isEmpty(values)) {
            values = session;
        }

        if (isReAuthenticateRoute) {
            configureReauthenticateSession();
        } else if (values && (values.token || values.source)) {
            updateSessionToken(values.token, location.pathname, values.source);
        } else if (onMount && !session.token) {
            /*
                Chrome blocks window.location.href navigation in useEffect when there's no user gesture, causing the "BeforeUnloadNoGesture" error.
                This is a security measure to prevent sites from trapping users.
                see - https://chromestatus.com/feature/5082396709879808

                - Due to this, it cause a production error (https://authenticating.slack.com/archives/C01MSKJ5TAT/p1764879832953999)
                - hence using a replace navigation instead of window.location.href
            */
            navigate('/session-expired?session=invalid', { replace: true });
        }
    }, []);

    // if Medallion token or Reauthenticate sessionUUID available then load the component
    return onMount || session.token || reauthenticateSession ? (
        children
    ) : (
        <Navigate to="/session-expired?session=invalid" replace />
    );
};

RequireSession.propTypes = {
    children: PropTypes.node,
    updateSessionToken: PropTypes.func,
    updateReauthenticateSessionID: PropTypes.func,
    session: PropTypes.object,
    reauthenticateSession: PropTypes.string,
    onMount: PropTypes.bool,
};

export default RequireSession;
